import clsx from "clsx";
import { useState } from "react";

import { Snackbar } from "@chef/components";
import { formatDate, isMealboxBasketType } from "@chef/helpers";
import { Loading } from "@chef/icons/small";
import { useCreateOrUpdateMultipleSchedulersMutation } from "@chef/state-management";
import { nonNullable } from "@chef/utils/nonNullable";

import { useMyAccountCalendarQuery } from "../hooks/useMyAccountCalendarQuery";
import { findFirstContinuouslyPausedCalendarWeeks } from "../utils";

import { intl } from "./DeliveriesPausedSnackbar.Intl";

export const DeliveriesPausedSnackbar = () => {
  const [optimisticHide, setOptimisticHide] = useState(false);

  const [
    createOrUpdateMultipleSchedulers,
    { isLoading: isUpdatingSchedulers },
  ] = useCreateOrUpdateMultipleSchedulersMutation();

  const { data: { calendar } = {} } = useMyAccountCalendarQuery();

  const resumeAndHide = async (
    calendarWeeks: {
      week: number;
      year: number;
    }[],
  ) => {
    try {
      setOptimisticHide(true);

      const data = calendarWeeks.map(({ week, year }) => ({
        week,
        year,
        delivery: true,
      }));

      await createOrUpdateMultipleSchedulers({ schedulers: data });
    } catch (err) {
      console.error(err);
      setOptimisticHide(false);
    }
  };

  const continuouslyPausedCalendarWeeks =
    findFirstContinuouslyPausedCalendarWeeks(calendar);
  if (
    !continuouslyPausedCalendarWeeks ||
    continuouslyPausedCalendarWeeks.length < 2
  ) {
    return null;
  }

  const deliveries = continuouslyPausedCalendarWeeks
    .map((calendarWeek) => {
      const basket = calendarWeek.baskets.find(isMealboxBasketType);

      return basket?.shippingDetails.plannedDeliveryInformation;
    })
    .filter(nonNullable);

  const first = deliveries[0];
  const last = deliveries[deliveries.length - 1];

  const firstDateString = formatDate(new Date(first.deliveryDate), "d. LLL");
  const lastDateString = formatDate(new Date(last.deliveryDate), "d. LLL");

  const label = intl.DELIVERIES_PAUSED_IN_PERIOD({
    firstDateString,
    lastDateString,
  });

  if (optimisticHide) {
    return null;
  }

  return (
    <Snackbar warning showIcon={false} className="mb-4 lg:mb-6">
      <p>{label}</p>
      <button
        className="self-start underline"
        onClick={() => resumeAndHide(continuouslyPausedCalendarWeeks)}
        disabled={isUpdatingSchedulers}
      >
        {isUpdatingSchedulers && <Loading className="mr-2 animate-spin" />}
        <strong className={clsx(isUpdatingSchedulers && "opacity-50")}>
          {intl.RESUME_DELIVERIES}
        </strong>
      </button>
    </Snackbar>
  );
};
